'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { BookOpen, Video, FileText, Layers, Lightbulb, HelpCircle, Search, ChevronRight, Sparkles, Zap, Target, Play, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function ResourceCenterContent() {
  const { t, language } = useLanguage();
  const rc = t?.resourceCenter;
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Build localized categories from translation keys
  const CATEGORIES = [
    {
      id: 'tutorials',
      label: rc?.catTutorials ?? 'Video Tutorials',
      icon: Video,
      color: 'text-red-400',
      bg: 'bg-red-400/10',
      border: 'border-red-400/20',
      resources: [
        { title: rc?.tut1Title ?? 'Getting Started with AIMaker Studio', desc: rc?.tut1Desc ?? 'A complete walkthrough of the platform for new users.', tag: rc?.tagBeginner ?? 'Beginner', tagKey: 'Beginner', href: '#', comingSoon: true },
        { title: rc?.tut2Title ?? 'DreamDNA™ Deep Dive', desc: rc?.tut2Desc ?? 'Learn how to get the most out of your DreamDNA analysis.', tag: rc?.tagIntermediate ?? 'Intermediate', tagKey: 'Intermediate', href: '#', comingSoon: true },
        { title: rc?.tut3Title ?? 'Business Builder Masterclass', desc: rc?.tut3Desc ?? 'Build a complete business plan using AI in under 30 minutes.', tag: rc?.tagAdvanced ?? 'Advanced', tagKey: 'Advanced', href: '#', comingSoon: true },
        { title: rc?.tut4Title ?? 'Visual Studio Essentials', desc: rc?.tut4Desc ?? 'Create stunning images, logos, and brand assets with AI.', tag: rc?.tagBeginner ?? 'Beginner', tagKey: 'Beginner', href: '#', comingSoon: true },
      ],
    },
    {
      id: 'docs',
      label: rc?.catDocs ?? 'Documentation',
      icon: FileText,
      color: 'text-blue-400',
      bg: 'bg-blue-400/10',
      border: 'border-blue-400/20',
      resources: [
        { title: rc?.doc1Title ?? 'Platform Overview', desc: rc?.doc1Desc ?? 'Understand all the tools and features available in AIMaker Studio.', tag: rc?.tagReference ?? 'Reference', tagKey: 'Reference', href: '/help-center' },
        { title: rc?.doc2Title ?? 'Credit System Guide', desc: rc?.doc2Desc ?? 'How credits work, what they cost, and how to maximize them.', tag: rc?.tagReference ?? 'Reference', tagKey: 'Reference', href: '/credits' },
        { title: rc?.doc3Title ?? 'AI Generation Guide', desc: rc?.doc3Desc ?? 'Best practices for writing prompts that get great results.', tag: rc?.tagGuide ?? 'Guide', tagKey: 'Guide', href: '/help-center#ai-generation' },
        { title: rc?.doc4Title ?? 'Privacy & Data Guide', desc: rc?.doc4Desc ?? 'How your data is stored, used, and protected.', tag: rc?.tagLegal ?? 'Legal', tagKey: 'Legal', href: '/privacy-policy' },
      ],
    },
    {
      id: 'templates',
      label: rc?.catTemplates ?? 'Templates',
      icon: Layers,
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
      border: 'border-purple-400/20',
      resources: [
        { title: rc?.tmpl1Title ?? 'Business Plan Templates', desc: rc?.tmpl1Desc ?? 'Ready-to-use templates for common business types.', tag: rc?.tagTemplate ?? 'Template', tagKey: 'Template', href: '/templates' },
        { title: rc?.tmpl2Title ?? 'Brand Identity Templates', desc: rc?.tmpl2Desc ?? 'Color palettes, typography, and brand guidelines.', tag: rc?.tagTemplate ?? 'Template', tagKey: 'Template', href: '/templates?category=brand' },
        { title: rc?.tmpl3Title ?? 'Social Media Templates', desc: rc?.tmpl3Desc ?? 'Post templates for Instagram, Facebook, and more.', tag: rc?.tagTemplate ?? 'Template', tagKey: 'Template', href: '/social-media-studio' },
        { title: rc?.tmpl4Title ?? 'Marketing Copy Templates', desc: rc?.tmpl4Desc ?? 'Headlines, CTAs, and email templates.', tag: rc?.tagTemplate ?? 'Template', tagKey: 'Template', href: '/templates?category=marketing' },
      ],
    },
    {
      id: 'prompts',
      label: rc?.catPrompts ?? 'Prompt Library',
      icon: Sparkles,
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10',
      border: 'border-yellow-400/20',
      resources: [
        { title: rc?.prm1Title ?? 'Business Name Generator Prompts', desc: rc?.prm1Desc ?? 'Proven prompts for generating memorable business names.', tag: rc?.tagPrompt ?? 'Prompt', tagKey: 'Prompt', href: '/create-page?type=business-name' },
        { title: rc?.prm2Title ?? 'Logo Design Prompts', desc: rc?.prm2Desc ?? 'Detailed prompts for creating professional logos.', tag: rc?.tagPrompt ?? 'Prompt', tagKey: 'Prompt', href: '/image-studio' },
        { title: rc?.prm3Title ?? 'Marketing Copy Prompts', desc: rc?.prm3Desc ?? 'High-converting copy prompts for ads and landing pages.', tag: rc?.tagPrompt ?? 'Prompt', tagKey: 'Prompt', href: '/create-page?type=marketing' },
        { title: rc?.prm4Title ?? 'Social Media Caption Prompts', desc: rc?.prm4Desc ?? 'Engaging captions for every platform and niche.', tag: rc?.tagPrompt ?? 'Prompt', tagKey: 'Prompt', href: '/social-media-studio' },
      ],
    },
    {
      id: 'guides',
      label: rc?.catGuides ?? 'Business Guides',
      icon: BookOpen,
      color: 'text-green-400',
      bg: 'bg-green-400/10',
      border: 'border-green-400/20',
      resources: [
        { title: rc?.gd1Title ?? 'How to Launch Your First Business', desc: rc?.gd1Desc ?? 'A step-by-step guide from idea to launch using AIMaker Studio.', tag: rc?.tagGuide ?? 'Guide', tagKey: 'Guide', href: '/business-builder' },
        { title: rc?.gd2Title ?? 'Building Your Brand Identity', desc: rc?.gd2Desc ?? 'Everything you need to create a cohesive, professional brand.', tag: rc?.tagGuide ?? 'Guide', tagKey: 'Guide', href: '/brand-refresh' },
        { title: rc?.gd3Title ?? 'Content Marketing for Beginners', desc: rc?.gd3Desc ?? 'How to create content that attracts and converts customers.', tag: rc?.tagGuide ?? 'Guide', tagKey: 'Guide', href: '/social-media-studio' },
        { title: rc?.gd4Title ?? 'E-Commerce Setup Guide', desc: rc?.gd4Desc ?? 'Launch your online store with AI-generated assets and copy.', tag: rc?.tagGuide ?? 'Guide', tagKey: 'Guide', href: '/business-builder?section=ecommerce' },
      ],
    },
    {
      id: 'tips',
      label: rc?.catTips ?? 'AI Tips',
      icon: Lightbulb,
      color: 'text-orange-400',
      bg: 'bg-orange-400/10',
      border: 'border-orange-400/20',
      resources: [
        { title: rc?.tip1Title ?? '10 Tips for Better AI Prompts', desc: rc?.tip1Desc ?? 'Simple techniques to dramatically improve your AI results.', tag: rc?.tagTip ?? 'Tip', tagKey: 'Tip', href: '#', comingSoon: true },
        { title: rc?.tip2Title ?? 'How to Use DreamDNA™ Effectively', desc: rc?.tip2Desc ?? 'Get the most accurate and actionable DreamDNA analysis.', tag: rc?.tagTip ?? 'Tip', tagKey: 'Tip', href: '/dream-dashboard' },
        { title: rc?.tip3Title ?? 'Maximizing Your Credits', desc: rc?.tip3Desc ?? 'Smart strategies to get more value from your credit balance.', tag: rc?.tagTip ?? 'Tip', tagKey: 'Tip', href: '/credits' },
        { title: rc?.tip4Title ?? 'AI Image Generation Best Practices', desc: rc?.tip4Desc ?? 'Create stunning visuals with these proven techniques.', tag: rc?.tagTip ?? 'Tip', tagKey: 'Tip', href: '/image-studio' },
      ],
    },
    {
      id: 'faq',
      label: rc?.catFaq ?? 'FAQ',
      icon: HelpCircle,
      color: 'text-cyan-400',
      bg: 'bg-cyan-400/10',
      border: 'border-cyan-400/20',
      resources: [
        { title: rc?.faq1Title ?? 'Frequently Asked Questions', desc: rc?.faq1Desc ?? 'Answers to the most common questions about AIMaker Studio.', tag: rc?.tagFaq ?? 'FAQ', tagKey: 'FAQ', href: '/help-center#faq' },
        { title: rc?.faq2Title ?? 'Billing & Subscription FAQ', desc: rc?.faq2Desc ?? 'Everything you need to know about plans, credits, and billing.', tag: rc?.tagFaq ?? 'FAQ', tagKey: 'FAQ', href: '/pricing' },
        { title: rc?.faq3Title ?? 'AI Generation FAQ', desc: rc?.faq3Desc ?? 'Common questions about how AI generations work.', tag: rc?.tagFaq ?? 'FAQ', tagKey: 'FAQ', href: '/help-center#ai-faq' },
        { title: rc?.faq4Title ?? 'Account & Security FAQ', desc: rc?.faq4Desc ?? 'Questions about your account, data, and privacy.', tag: rc?.tagFaq ?? 'FAQ', tagKey: 'FAQ', href: '/security' },
      ],
    },
  ];

  const TAG_COLORS: Record<string, string> = {
    Beginner: 'bg-green-400/10 text-green-400',
    Intermediate: 'bg-yellow-400/10 text-yellow-400',
    Advanced: 'bg-red-400/10 text-red-400',
    Reference: 'bg-blue-400/10 text-blue-400',
    Guide: 'bg-purple-400/10 text-purple-400',
    Template: 'bg-primary/10 text-primary',
    Prompt: 'bg-yellow-400/10 text-yellow-400',
    Tip: 'bg-orange-400/10 text-orange-400',
    FAQ: 'bg-cyan-400/10 text-cyan-400',
    Legal: 'bg-muted text-muted-foreground',
  };

  const filteredCategories = CATEGORIES.filter(cat =>
    activeCategory === 'all' || cat.id === activeCategory
  ).map(cat => ({
    ...cat,
    resources: cat.resources.filter(r =>
      !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.desc.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.resources.length > 0);

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="section-fade-in section-fade-in-0">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={20} className="text-primary" />
          <h2 className="text-2xl font-700 text-foreground">{rc?.title ?? 'Resource Center'}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {rc?.subtitle ?? 'Everything you need to master AIMaker Studio and build your dream business.'}
        </p>
      </div>

      {/* Search + Filter */}
      <div className="section-fade-in section-fade-in-1 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={rc?.searchPlaceholder ?? 'Search resources, guides, tips...'}
            className="input-dark w-full pl-9 pr-4 py-2.5 rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-2 rounded-lg text-xs font-600 transition-all ${activeCategory === 'all' ? 'tab-active' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}
          >
            {rc?.filterAll ?? 'All'}
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3 py-2 rounded-lg text-xs font-600 transition-all ${activeCategory === cat.id ? 'tab-active' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Quick Links */}
      {activeCategory === 'all' && !search && (
        <div className="section-fade-in section-fade-in-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Play, label: rc?.quickStartLabel ?? 'Quick Start Guide', desc: rc?.quickStartDesc ?? 'Get up and running in 5 minutes', href: '#', color: 'text-green-400', bg: 'bg-green-400/10', comingSoon: true },
            { icon: Zap, label: rc?.aiTipsLabel ?? 'AI Tips & Tricks', desc: rc?.aiTipsDesc ?? 'Level up your AI generations', href: '#', color: 'text-yellow-400', bg: 'bg-yellow-400/10', comingSoon: true },
            { icon: Target, label: rc?.businessGuidesLabel ?? 'Business Guides', desc: rc?.businessGuidesDesc ?? 'From idea to launch', href: '/business-builder', color: 'text-primary', bg: 'bg-primary/10', comingSoon: false },
          ].map(item => {
            const ItemIcon = item.icon;
            const comingSoonLabel =
              language === 'es' ? 'Próximamente' : language === 'pt' ? 'Em breve' : 'Coming soon';
            const cardClass =
              'flex items-center gap-4 p-4 rounded-xl bg-card border border-border transition-all group';
            const inner = (
              <>
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
                  <ItemIcon size={18} className={item.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className={`text-sm font-600 text-foreground ${item.comingSoon ? '' : 'group-hover:text-primary'} transition-colors`}>{item.label}</p>
                    {item.comingSoon && (
                      <span className="text-[9px] font-700 uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                        {comingSoonLabel}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                {!item.comingSoon && (
                  <ArrowRight size={14} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                )}
              </>
            );
            if (item.comingSoon) {
              return (
                <div key={item.label} className={`${cardClass} opacity-80 cursor-default`}>
                  {inner}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`${cardClass} hover:border-primary/30`}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      )}

      {/* Resource Categories */}
      {filteredCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search size={32} className="text-muted-foreground/30 mb-3" />
          <p className="text-base font-600 text-foreground mb-1">{rc?.noResultsTitle ?? 'No resources found'}</p>
          <p className="text-sm text-muted-foreground">{rc?.noResultsDesc ?? 'Try a different search term or browse all categories.'}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {filteredCategories.map((cat, catIdx) => {
            const CatIcon = cat.icon;
            return (
              <div key={cat.id} className={`section-fade-in section-fade-in-${catIdx + 3}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-lg ${cat.bg} flex items-center justify-center`}>
                    <CatIcon size={16} className={cat.color} />
                  </div>
                  <h3 className="text-base font-700 text-foreground">{cat.label}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  {cat.resources.map((resource, rIdx) => {
                    const comingSoon = Boolean((resource as { comingSoon?: boolean }).comingSoon);
                    const comingSoonLabel = rc?.tagComingSoon ??
                      (language === 'es' ? 'Próximamente' : language === 'pt' ? 'Em breve' : 'Coming soon');
                    const body = (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full ${TAG_COLORS[resource.tagKey] || 'bg-muted text-muted-foreground'}`}>
                              {resource.tag}
                            </span>
                            {comingSoon && (
                              <span className="text-[9px] font-700 uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                                {comingSoonLabel}
                              </span>
                            )}
                          </div>
                          {!comingSoon && (
                            <ChevronRight size={13} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-600 text-foreground mb-1 ${comingSoon ? '' : 'group-hover:text-primary'} transition-colors`}>{resource.title}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{resource.desc}</p>
                        </div>
                      </>
                    );
                    if (comingSoon) {
                      return (
                        <div
                          key={rIdx}
                          className="flex flex-col gap-3 p-4 rounded-xl bg-card border border-border opacity-80 cursor-default"
                          aria-disabled="true"
                        >
                          {body}
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={rIdx}
                        href={resource.href}
                        className="flex flex-col gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 hover:-translate-y-0.5 transition-all group"
                      >
                        {body}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom CTA */}
      <div className="section-fade-in mt-4 p-6 rounded-xl bg-primary/5 border border-primary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-base font-700 text-foreground mb-1">{rc?.ctaTitle ?? "Can't find what you're looking for?"}</p>
          <p className="text-sm text-muted-foreground">{rc?.ctaDesc ?? 'Our support team is here to help you succeed.'}</p>
        </div>
        <Link
          href="/contact"
          className="btn-primary px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 shrink-0"
        >
          <HelpCircle size={14} />
          {rc?.ctaBtn ?? 'Contact Support'}
        </Link>
      </div>
    </div>
  );
}
